/**
 * RoadmapCard — one draggable kanban card.
 *
 * Drag and click share the same element, so the two have to be told apart:
 * dnd-kit fires `onClick` after a drag ends as well as after a real click. We
 * gate the click on dnd-kit's own `isDragging` plus a small pointer-movement
 * threshold, so a click that wobbled a couple of pixels still opens the editor
 * while a genuine drag doesn't.
 *
 * No comment-count badge: `RoadmapItem` carries no `commentCount` and comments
 * live in a subcollection, so showing one would mean an aggregate query per
 * card. Wants a server-maintained counter first.
 */
import { useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  ownerLabel,
  priorityBadgeClass,
  scopeBadgeClass,
} from './roadmapMeta';
import type { RoadmapItem } from '../../types';

/** Tags beyond this are collapsed into a '+N' chip. */
const MAX_VISIBLE_TAGS = 3;

/**
 * Pointer travel (px) that separates a click from a drag. The kanban's
 * PointerSensor uses this same number as its activation distance: below it no
 * drag starts so the click must open the editor, at or above it a drag started
 * so the click must be swallowed. One constant, no dead zone in between.
 */
export const DRAG_ACTIVATION_DISTANCE = 5;

interface RoadmapCardProps {
  item: RoadmapItem;
  onOpen: (item: RoadmapItem) => void;
  /** True while this card's status write is in flight. */
  saving: boolean;
}

export function RoadmapCard({ item, onOpen, saving }: RoadmapCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: item.id, disabled: saving });

  const pointerStart = useRef<{ x: number; y: number } | null>(null);

  const tags = item.tags ?? [];
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const overflowCount = tags.length - visibleTags.length;

  const handlePointerDown = (e: React.PointerEvent) => {
    pointerStart.current = { x: e.clientX, y: e.clientY };
  };

  const handleClick = (e: React.MouseEvent) => {
    // A drag that ended on this card also produces a click — ignore it.
    if (isDragging) return;
    const start = pointerStart.current;
    if (start) {
      const dx = Math.abs(e.clientX - start.x);
      const dy = Math.abs(e.clientY - start.y);
      if (dx >= DRAG_ACTIVATION_DISTANCE || dy >= DRAG_ACTIVATION_DISTANCE) {
        return;
      }
    }
    onOpen(item);
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        // Lift the card under the cursor without disturbing sibling layout.
        transform: CSS.Translate.toString(transform),
      }}
      {...listeners}
      {...attributes}
      onPointerDown={(e) => {
        handlePointerDown(e);
        listeners?.onPointerDown?.(e);
      }}
      onClick={handleClick}
      className={`rounded-lg border bg-white p-2.5 space-y-2 text-left w-full ${
        isDragging
          ? 'border-brand-400 shadow-lg opacity-90 z-10 relative cursor-grabbing'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm cursor-grab'
      } ${saving ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start gap-2">
        <span
          className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold ${priorityBadgeClass(item.priority)}`}
        >
          {item.priority ?? '—'}
        </span>
        <span className="text-sm font-medium text-gray-800 line-clamp-2 flex-1">
          {(item.title ?? '').trim() || (
            <span className="text-gray-300">(untitled)</span>
          )}
        </span>
      </div>

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {visibleTags.map((tag) => (
            <span
              key={tag}
              className="px-1.5 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-600"
            >
              {tag}
            </span>
          ))}
          {overflowCount > 0 && (
            <span
              className="px-1.5 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-500"
              title={tags.slice(MAX_VISIBLE_TAGS).join(', ')}
            >
              +{overflowCount}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 text-[11px] text-gray-500">
        <span className="truncate">{ownerLabel(item.owner)}</span>
        {item.estimatedScope && (
          <span
            className={`ml-auto shrink-0 px-1.5 py-0.5 rounded font-medium ${scopeBadgeClass(item.estimatedScope)}`}
          >
            {item.estimatedScope}
          </span>
        )}
      </div>
    </div>
  );
}
